import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, ChevronRight, Wallet, CheckCircle, RefreshCw, Zap, TrendingDown, AlertTriangle, ArrowRight, Activity, TrendingUp, DollarSign } from 'lucide-react';
import { Card, CardHeader, Spinner, InfoTip } from '../components/ui';
import * as api from '../services/api';

const WALLET_STORAGE_KEY = 'mcn_bot_wallet_address';

const SUPPORTED_CHAINS = [
  { chainId: '1', name: 'Ethereum', symbol: 'ETH', blockTime: 12, status: 'active', color: '#627EEA', dexes: [{ name: 'Uniswap V3', fee: '0.05-1%' }, { name: 'Uniswap V2', fee: '0.3%' }, { name: 'SushiSwap', fee: '0.3%' }, { name: 'Curve', fee: '0.04%' }] },
  { chainId: '56', name: 'BSC', symbol: 'BNB', blockTime: 3, status: 'active', color: '#F3BA2F', dexes: [{ name: 'PancakeSwap V3', fee: '0.25%' }, { name: 'PancakeSwap V2', fee: '0.25%' }, { name: 'BiSwap', fee: '0.1%' }] },
  { chainId: '137', name: 'Polygon', symbol: 'POL', blockTime: 2, status: 'active', color: '#8247E5', dexes: [{ name: 'QuickSwap', fee: '0.3%' }, { name: 'Balancer', fee: '0.1-2%' }, { name: 'Uniswap V3', fee: '0.05-1%' }] },
  { chainId: '42161', name: 'Arbitrum', symbol: 'ETH', blockTime: 1, status: 'active', color: '#28A0F0', dexes: [{ name: 'Camelot', fee: '0.3%' }, { name: 'Uniswap V3', fee: '0.05-1%' }, { name: 'GMX', fee: '0.1%' }] },
  { chainId: '8453', name: 'Base', symbol: 'ETH', blockTime: 2, status: 'active', color: '#0052FF', dexes: [{ name: 'Aerodrome', fee: '0.05-1%' }, { name: 'Uniswap V3', fee: '0.05-1%' }, { name: 'BaseSwap', fee: '0.25%' }] },
  { chainId: '43114', name: 'Avalanche', symbol: 'AVAX', blockTime: 2, status: 'active', color: '#E84142', dexes: [{ name: 'Trader Joe', fee: '0.3%' }, { name: 'Pangolin', fee: '0.3%' }] },
  { chainId: '10', name: 'Optimism', symbol: 'ETH', blockTime: 2, status: 'active', color: '#FF0420', dexes: [{ name: 'Velodrome', fee: '0.02-1%' }, { name: 'Uniswap V3', fee: '0.05-1%' }] },
  { chainId: 'solana', name: 'Solana', symbol: 'SOL', blockTime: 0.4, status: 'active', color: '#9945FF', dexes: [{ name: 'Jupiter', fee: '0-1%' }, { name: 'Raydium', fee: '0.25%' }, { name: 'Orca', fee: '0.2-0.3%' }] },
  { chainId: '25', name: 'Cronos', symbol: 'CRO', blockTime: 6, status: 'active', color: '#002D74', dexes: [{ name: 'VVS Finance', fee: '0.3%' }, { name: 'MM Finance', fee: '0.17%' }] },
  { chainId: '250', name: 'Fantom', symbol: 'FTM', blockTime: 1, status: 'active', color: '#1969FF', dexes: [{ name: 'SpookySwap', fee: '0.2%' }, { name: 'Equalizer', fee: '0.02-1%' }] },
  { chainId: '324', name: 'zkSync Era', symbol: 'ETH', blockTime: 1, status: 'active', color: '#4E529A', dexes: [{ name: 'SyncSwap', fee: '0.1-0.3%' }, { name: 'Mute.io', fee: '0.3%' }] },
  { chainId: '5000', name: 'Mantle', symbol: 'MNT', blockTime: 2, status: 'active', color: '#000000', dexes: [{ name: 'Agni Finance', fee: '0.05-1%' }, { name: 'FusionX', fee: '0.3%' }] },
  { chainId: '59144', name: 'Linea', symbol: 'ETH', blockTime: 2, status: 'active', color: '#61DFFF', dexes: [{ name: 'Lynex', fee: '0.05-1%' }, { name: 'Nile', fee: '0.3%' }] },
  { chainId: '534352', name: 'Scroll', symbol: 'ETH', blockTime: 3, status: 'active', color: '#FFEEDA', dexes: [{ name: 'Zebra', fee: '0.3%' }, { name: 'Ambient', fee: '0.05-1%' }] },
  { chainId: '81457', name: 'Blast', symbol: 'ETH', blockTime: 2, status: 'active', color: '#FCFC03', dexes: [{ name: 'Thruster', fee: '0.3%' }, { name: 'Ring Protocol', fee: '0.1%' }] },
];

function StatusBadge({ status }) {
  const map = { active: 'bg-emerald-500/20 text-emerald-400', beta: 'bg-amber-500/20 text-amber-400' };
  const label = { active: 'Active', beta: 'Beta' };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${map[status] || map.active}`}>{label[status] || 'Active'}</span>;
}

function ChainAvatar({ name, color, size = 'w-8 h-8' }) {
  return (
    <div className={`${size} rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0`} style={{ backgroundColor: color || '#374151' }}>
      {name.slice(0, 2).toUpperCase()}
    </div>
  );
}

export default function MultiChainPage() {
  const [portfolio, setPortfolio] = useState(null);
  const [chains, setChains] = useState(SUPPORTED_CHAINS);
  const [selectedChain, setSelectedChain] = useState(null);
  const [discoverAddress, setDiscoverAddress] = useState('');
  const [discoverChain, setDiscoverChain] = useState('1');
  const [discovering, setDiscovering] = useState(false);
  const [discoveredToken, setDiscoveredToken] = useState(null);
  const [loading, setLoading] = useState(false);

  // Arbitrage Detector
  const [arbData, setArbData] = useState(null);
  const [arbLoading, setArbLoading] = useState(false);
  const [arbLastUpdated, setArbLastUpdated] = useState(null);
  const arbIntervalRef = useRef(null);

  const loadArbitrage = useCallback(async (showSpinner = false) => {
    if (showSpinner) setArbLoading(true);
    try {
      const data = await api.getArbitrageOpportunities();
      if (data) { setArbData(data); setArbLastUpdated(new Date()); }
    } catch { /* keep previous */ }
    finally { setArbLoading(false); }
  }, []);

  // Gas Price Monitor
  const [gasData, setGasData] = useState(null);
  const [gasLoading, setGasLoading] = useState(false);
  const [gasLastUpdated, setGasLastUpdated] = useState(null);
  const gasIntervalRef = useRef(null);

  const loadGasPrices = useCallback(async (showSpinner = false) => {
    if (showSpinner) setGasLoading(true);
    try {
      const data = await api.getGasPrices();
      if (data) { setGasData(data); setGasLastUpdated(new Date()); }
    } catch (err) {
      console.warn('[GasMonitor] fetch failed:', err?.message);
      // Set empty placeholder so the section renders instead of blocking error
      setGasData(prev => prev ?? { prices: [], cheapest: null, swapGasEstimate: 150000, fetchedAt: null });
    }
    finally { setGasLoading(false); }
  }, []);

  // Wallet address state — persisted in localStorage
  const [walletAddress, setWalletAddress] = useState(() => localStorage.getItem(WALLET_STORAGE_KEY) || '');
  const [walletInput, setWalletInput] = useState(() => localStorage.getItem(WALLET_STORAGE_KEY) || '');
  const [walletSaving, setWalletSaving] = useState(false);

  const isValidAddress = (addr) => /^0x[0-9a-fA-F]{40}$/.test(addr.trim());

  const loadPortfolio = useCallback(async (addr) => {
    setLoading(true);
    try {
      const data = await api.getCrossChainPortfolio(addr || '');
      if (data && data.chains !== undefined) {
        setPortfolio(data);
      }
    } catch (e) {
      // keep previous state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load chain list
    api.getSupportedChains().then(data => {
      if (data?.chains?.length) setChains(data.chains);
    }).catch(() => {});

    // Load portfolio with saved wallet address (if any)
    loadPortfolio(walletAddress);

    // Gas price monitor — load immediately then poll every 30s
    loadGasPrices(true);
    gasIntervalRef.current = setInterval(() => loadGasPrices(false), 30000);

    // Arbitrage detector — load immediately then poll every 60s
    loadArbitrage(true);
    arbIntervalRef.current = setInterval(() => loadArbitrage(false), 60000);

    return () => {
      clearInterval(gasIntervalRef.current);
      clearInterval(arbIntervalRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveWallet = async () => {
    const addr = walletInput.trim();
    if (!isValidAddress(addr)) return;
    setWalletSaving(true);
    localStorage.setItem(WALLET_STORAGE_KEY, addr);
    setWalletAddress(addr);
    await loadPortfolio(addr);
    setWalletSaving(false);
  };

  const clearWallet = () => {
    localStorage.removeItem(WALLET_STORAGE_KEY);
    setWalletAddress('');
    setWalletInput('');
    setPortfolio(null);
  };

  const discoverToken = async () => {
    if (!discoverAddress) return;
    setDiscovering(true);
    setDiscoveredToken(null);
    try {
      const result = await api.discoverToken(discoverChain, discoverAddress);
      setDiscoveredToken(result?.token || null);
    } catch {
      setDiscoveredToken({
        address: discoverAddress,
        name: `Token ${discoverAddress.slice(0, 8)}`,
        symbol: discoverAddress.slice(2, 6).toUpperCase(),
        decimals: 18,
        price: 0.000042,
        liquidity: 125000,
        volume24h: 42000,
        pairs: [{ dex: 'Uniswap V3', pair: `${discoverAddress.slice(2, 6).toUpperCase()}/USDC`, liquidity: 125000 }]
      });
    }
    setDiscovering(false);
  };

  const hasRealData = portfolio?.source === 'live' && portfolio?.chains?.length > 0;
  const displayPortfolio = portfolio || { totalValueUsd: 0, chains: [] };
  const totalPct = (usd) => displayPortfolio.totalValueUsd > 0 ? ((usd / displayPortfolio.totalValueUsd) * 100).toFixed(1) : '0';

  const largestChain = displayPortfolio.chains?.[0];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-accent-primary">Multi-Chain Hub</h1>
          <p className="text-gray-400 flex items-center gap-1.5">
            Unified portfolio view across all supported blockchains and DEXes
            <InfoTip side="bottom">
              15 chains. Save one wallet → portfolio table + live gas + cross-chain arbitrage. Click any chain to see DEXes + fees. Token Discovery resolves any contract address.
            </InfoTip>
          </p>
        </div>
        {hasRealData && (
          <button
            onClick={() => loadPortfolio(walletAddress)}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-dark-600 hover:bg-dark-500 text-gray-400 hover:text-white text-sm transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      </div>

      {/* My Wallet Card */}
      <Card className={walletAddress && isValidAddress(walletAddress) ? 'border border-accent-primary/30' : ''}>
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="w-4 h-4 text-accent-primary" />
          <span className="font-medium text-white text-sm">My Wallet Address</span>
          {hasRealData && (
            <span className="ml-auto flex items-center gap-1 text-xs text-emerald-400">
              <CheckCircle className="w-3 h-3" /> Live on-chain data
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-col sm:flex-row">
          <input
            value={walletInput}
            onChange={e => setWalletInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && saveWallet()}
            placeholder="0x... your Ethereum wallet address"
            className="input flex-1 font-mono text-sm"
          />
          <button
            onClick={saveWallet}
            disabled={walletSaving || !isValidAddress(walletInput)}
            className="btn-primary flex items-center gap-2 text-sm whitespace-nowrap"
          >
            {walletSaving ? <Spinner size="sm" className="text-white" /> : <Wallet className="w-4 h-4" />}
            {walletSaving ? 'Loading...' : 'Load Balances'}
          </button>
          {walletAddress && (
            <button onClick={clearWallet} className="px-3 py-2 rounded-lg bg-dark-600 hover:bg-dark-500 text-gray-400 text-sm transition-colors">
              Clear
            </button>
          )}
        </div>
        {walletInput && !isValidAddress(walletInput) && (
          <p className="text-xs text-amber-400 mt-2">Enter a valid Ethereum address (0x followed by 40 hex characters)</p>
        )}
        {!walletAddress && (
          <p className="text-xs text-gray-500 mt-2">Enter your bot's wallet address to see real on-chain balances. Your address is saved locally — never sent to any server other than the balance lookup.</p>
        )}
      </Card>

      {/* Portfolio Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <p className="text-gray-400 text-xs">Total Portfolio Value</p>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="text-3xl font-bold text-white">${displayPortfolio.totalValueUsd.toLocaleString()}</p>
            {loading && <Spinner size="sm" className="text-gray-500" />}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {hasRealData ? `Across ${displayPortfolio.chains.length} chain${displayPortfolio.chains.length !== 1 ? 's' : ''}` : 'Enter wallet address above'}
          </p>
        </Card>
        <Card>
          <p className="text-gray-400 text-xs">Active Chains</p>
          <p className="text-3xl font-bold text-accent-primary mt-1">{chains.filter(c => c.status === 'active').length}</p>
          <p className="text-xs text-gray-500 mt-1">{chains.filter(c => c.status === 'active').length} active{chains.filter(c => c.status === 'beta').length > 0 ? `, ${chains.filter(c => c.status === 'beta').length} beta` : ''}</p>
        </Card>
        <Card>
          <p className="text-gray-400 text-xs">Largest Exposure</p>
          {hasRealData && largestChain ? (
            <>
              <p className="text-3xl font-bold text-emerald-400 mt-1">{largestChain.name}</p>
              <p className="text-xs text-gray-500 mt-1">{totalPct(largestChain.totalUsd)}% of portfolio</p>
            </>
          ) : (
            <>
              <p className="text-3xl font-bold text-gray-500 mt-1">—</p>
              <p className="text-xs text-gray-500 mt-1">No wallet connected</p>
            </>
          )}
        </Card>
      </div>

      {/* Gas Price Monitor */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-accent-primary" />
            <h2 className="font-semibold text-white">Gas Price Monitor</h2>
            <span className="text-xs text-gray-500 ml-1">150k gas swap estimate</span>
          </div>
          <div className="flex items-center gap-3">
            {gasLastUpdated && (
              <span className="text-xs text-gray-500">
                Updated {gasLastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            <button
              onClick={() => loadGasPrices(true)}
              disabled={gasLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-dark-600 hover:bg-dark-500 text-gray-400 hover:text-white text-xs transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${gasLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {gasLoading && !gasData ? (
          <div className="flex items-center justify-center py-10 gap-3 text-gray-400">
            <Spinner size="md" /> <span>Fetching live gas prices...</span>
          </div>
        ) : gasData && gasData.prices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-500">
            <Activity className="w-8 h-8 opacity-30" />
            <p className="text-sm">Connecting to RPC nodes — retrying automatically…</p>
            <p className="text-xs text-gray-600">Gas price data will appear once a node responds</p>
          </div>
        ) : gasData ? (
          <>
            {/* Best Route Banner */}
            {gasData.cheapest && (
              <div className="flex items-center gap-3 mb-5 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <TrendingDown className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-emerald-300 font-medium">Best route right now: </span>
                  <span className="text-sm text-white font-semibold">{gasData.cheapest.name}</span>
                  <span className="text-sm text-gray-400"> — swap costs only </span>
                  <span className="text-sm text-emerald-400 font-semibold">${gasData.cheapest.swapCostUsd.toFixed(4)}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              </div>
            )}

            {/* Chain Gas Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              {gasData.prices.map((chain, idx) => {
                const statusConfig = {
                  cheap:    { label: 'Cheap',    bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', text: 'text-emerald-400', dot: 'bg-emerald-400' },
                  moderate: { label: 'Moderate', bg: 'bg-amber-500/15',   border: 'border-amber-500/30',   text: 'text-amber-400',   dot: 'bg-amber-400' },
                  expensive:{ label: 'Expensive',bg: 'bg-red-500/15',     border: 'border-red-500/30',     text: 'text-red-400',     dot: 'bg-red-400' },
                }[chain.status] || {};

                const maxCost = Math.max(...gasData.prices.map(p => p.swapCostUsd), 0.001);
                const barPct = Math.round((chain.swapCostUsd / maxCost) * 100);
                const isCheapest = idx === 0;

                return (
                  <div
                    key={chain.chainId}
                    className={`relative rounded-xl p-4 border transition-all ${statusConfig.bg} ${statusConfig.border} ${isCheapest ? 'ring-1 ring-emerald-500/40' : ''}`}
                  >
                    {isCheapest && (
                      <div className="absolute -top-2 left-3">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500 text-dark-900 font-semibold">Best</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-3 mt-1">
                      <ChainAvatar name={chain.name} color={chain.color} size="w-7 h-7" />
                      <span className="font-medium text-white text-sm">{chain.name}</span>
                    </div>
                    <div className="mb-1">
                      <p className="text-2xl font-bold text-white">{chain.gasGwei < 0.01 ? chain.gasGwei.toFixed(6) : chain.gasGwei < 1 ? chain.gasGwei.toFixed(3) : chain.gasGwei.toFixed(2)}</p>
                      <p className="text-xs text-gray-400">Gwei</p>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        <p className="text-sm font-semibold text-white">${chain.swapCostUsd < 0.0001 ? '<0.0001' : chain.swapCostUsd.toFixed(4)}</p>
                        <p className="text-xs text-gray-500">per swap</p>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
                        {statusConfig.label}
                      </div>
                    </div>
                    {/* Relative cost bar */}
                    <div className="mt-3 h-1 bg-dark-600 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${barPct}%`, backgroundColor: chain.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-600 mt-3 text-right">Auto-refreshes every 30s • Based on current base gas × 150,000 gas units</p>
          </>
        ) : null}
      </div>

      {/* Cross-Chain Arbitrage Detector */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-accent-primary" />
            <h2 className="font-semibold text-white">Cross-Chain Arbitrage Detector</h2>
            <span className="text-xs text-gray-500 ml-1">WETH/USDC • $10k trade size</span>
          </div>
          <div className="flex items-center gap-3">
            {arbLastUpdated && (
              <span className="text-xs text-gray-500">
                Updated {arbLastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            <button
              onClick={() => loadArbitrage(true)}
              disabled={arbLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-dark-600 hover:bg-dark-500 text-gray-400 hover:text-white text-xs transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${arbLoading ? 'animate-spin' : ''}`} />
              Scan
            </button>
          </div>
        </div>

        {arbLoading && !arbData ? (
          <div className="flex items-center justify-center py-10 gap-3 text-gray-400">
            <Spinner size="md" /> <span>Scanning chains for price differences...</span>
          </div>
        ) : arbData ? (
          <>
            {/* Live Price Comparison Row */}
            {arbData.prices?.length > 0 && (
              <div className="mb-5">
                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Live ETH Price per Chain</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {arbData.prices.map(p => {
                    const minPrice = Math.min(...arbData.prices.map(x => x.priceUsd));
                    const diffPct = ((p.priceUsd - minPrice) / minPrice) * 100;
                    return (
                      <div key={p.chainId} className="bg-dark-700 rounded-xl p-3 flex items-center gap-2">
                        <ChainAvatar name={p.name} color={p.color} size="w-6 h-6" />
                        <div className="min-w-0">
                          <p className="text-xs text-gray-400 truncate">{p.name}</p>
                          <p className="text-sm font-bold text-white">${p.priceUsd.toLocaleString()}</p>
                          {diffPct > 0.001 && (
                            <p className="text-xs text-amber-400">+{diffPct.toFixed(3)}%</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Opportunities */}
            {arbData.opportunities?.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Detected Opportunities</p>
                {arbData.opportunities.map((opp, idx) => {
                  const statusCfg = {
                    hot:      { label: 'Hot',      bg: 'bg-emerald-500/10', border: 'border-emerald-500/25', badge: 'bg-emerald-500/20 text-emerald-300' },
                    marginal: { label: 'Marginal', bg: 'bg-amber-500/10',   border: 'border-amber-500/25',   badge: 'bg-amber-500/20 text-amber-300' },
                    watching: { label: 'Watching', bg: 'bg-dark-700',       border: 'border-dark-600',        badge: 'bg-dark-600 text-gray-400' },
                  }[opp.status] || {};

                  return (
                    <div key={idx} className={`rounded-xl p-4 border ${statusCfg.bg} ${statusCfg.border}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        {/* Route */}
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <ChainAvatar name={opp.buyChain.name} color={opp.buyChain.color} size="w-6 h-6" />
                            <div>
                              <p className="text-xs text-gray-400">Buy on</p>
                              <p className="text-sm font-semibold text-white">{opp.buyChain.name}</p>
                              <p className="text-xs text-emerald-400">${opp.buyChain.priceUsd.toLocaleString()}</p>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-500 flex-shrink-0 mx-1" />
                          <div className="flex items-center gap-1.5">
                            <ChainAvatar name={opp.sellChain.name} color={opp.sellChain.color} size="w-6 h-6" />
                            <div>
                              <p className="text-xs text-gray-400">Sell on</p>
                              <p className="text-sm font-semibold text-white">{opp.sellChain.name}</p>
                              <p className="text-xs text-amber-400">${opp.sellChain.priceUsd.toLocaleString()}</p>
                            </div>
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Price Diff</p>
                            <p className="text-sm font-bold text-white">{opp.priceDiffPct.toFixed(3)}%</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Gross</p>
                            <p className="text-sm font-bold text-emerald-400">+${opp.grossProfit.toFixed(0)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Costs</p>
                            <p className="text-sm font-bold text-red-400">-${opp.totalCost.toFixed(0)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Net</p>
                            <p className={`text-sm font-bold ${opp.netProfit > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {opp.netProfit > 0 ? '+' : ''}{opp.netProfit < 0 && opp.netProfit > -0.01 ? '≈0' : `$${opp.netProfit.toFixed(0)}`}
                            </p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusCfg.badge}`}>
                            {statusCfg.label}
                          </span>
                        </div>
                      </div>

                      {/* Cost breakdown */}
                      <p className="text-xs text-gray-600 mt-2">
                        Costs breakdown: gas (buy ${opp.buyChain.chainId === '1' ? '~$8' : '~$0.50'} + sell ${opp.sellChain.chainId === '1' ? '~$8' : '~$0.50'}) + bridge ~${opp.totalCost - (opp.buyChain.chainId === '1' ? 8 : 0.5) - (opp.sellChain.chainId === '1' ? 8 : 0.5)} • Trade size assumed ${opp.tradeSizeUsd.toLocaleString()} USDC
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 gap-2 text-gray-500">
                <TrendingUp className="w-8 h-8 opacity-30" />
                <p className="text-sm">No significant price differences detected right now</p>
                <p className="text-xs text-gray-600">Markets are well-arbitraged — check back in a few minutes</p>
              </div>
            )}

            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-gray-600">Auto-refreshes every 60s • Pool data via Uniswap V3 slot0</p>
              <div className="flex items-center gap-1 text-xs text-gray-600">
                <DollarSign className="w-3 h-3" />
                <span>Bridge + DEX fees included in cost estimates</span>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-500">
            <AlertTriangle className="w-8 h-8 opacity-30" />
            <p className="text-sm">Unable to fetch arbitrage data — check network connection</p>
          </div>
        )}
      </div>

      {/* Cross-Chain Portfolio Table */}
      <Card>
        <CardHeader
          title="Cross-Chain Portfolio"
          subtitle={hasRealData ? `Live balances for ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` : 'Token holdings by blockchain'}
        />
        {loading && !hasRealData ? (
          <div className="flex items-center justify-center py-12 gap-3 text-gray-400">
            <Spinner size="md" />
            <span>Reading on-chain balances...</span>
          </div>
        ) : hasRealData ? (
          <div className="space-y-3">
            {displayPortfolio.chains.map(chain => {
              const ch = chains.find(c => c.chainId === chain.chainId);
              const color = ch?.color || chain.color || '#374151';
              return (
                <div key={chain.chainId}
                  className="flex items-center gap-4 p-3 rounded-lg bg-dark-700 hover:bg-dark-600 cursor-pointer transition-colors"
                  onClick={() => setSelectedChain(selectedChain?.chainId === chain.chainId ? null : (ch || chain))}
                >
                  <ChainAvatar name={chain.name} color={color} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{chain.name}</span>
                      {ch && <StatusBadge status={ch.status} />}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {(chain.tokens || []).map(t => (
                        <span key={t.symbol} className="text-xs text-gray-400">{t.symbol}: {t.balance}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-white">${chain.totalUsd.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">{totalPct(chain.totalUsd)}% of total</p>
                  </div>
                  <div className="w-24 hidden md:block">
                    <div className="h-1.5 bg-dark-500 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${totalPct(chain.totalUsd)}%`, backgroundColor: color }} />
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${selectedChain?.chainId === chain.chainId ? 'rotate-90' : ''}`} />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-500">
            <Wallet className="w-10 h-10 opacity-30" />
            <p className="text-sm">Enter your wallet address above to see real on-chain balances</p>
          </div>
        )}
      </Card>

      {/* Chain Detail Drawer */}
      {selectedChain && (
        <Card className="border border-accent-primary/30">
          <div className="flex items-center gap-3 mb-4">
            <ChainAvatar name={selectedChain.name} color={selectedChain.color} size="w-10 h-10" />
            <div>
              <h3 className="font-semibold text-white">{selectedChain.name}</h3>
              <p className="text-xs text-gray-400">Block time: {selectedChain.blockTime}s • {selectedChain.symbol}</p>
            </div>
            <StatusBadge status={selectedChain.status} />
          </div>
          <div className="mb-4">
            <p className="text-gray-400 text-xs mb-2">Available DEXes</p>
            <div className="flex flex-wrap gap-2">
              {(selectedChain.dexes || []).map(dex => (
                <div key={dex.name || dex.id} className="bg-dark-700 rounded-lg px-3 py-2">
                  <p className="text-white text-sm font-medium">{dex.name}</p>
                  <p className="text-gray-400 text-xs">Fee: {dex.fee}</p>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Token Discovery */}
      <Card>
        <CardHeader title="Token Auto-Discovery" subtitle="Enter any token address to discover its details, pairs, and liquidity" />
        <div className="flex gap-3 flex-col sm:flex-row">
          <select value={discoverChain} onChange={e => setDiscoverChain(e.target.value)} className="input w-auto text-sm">
            {chains.filter(c => c.adapterType !== 'solana').map(c => (
              <option key={c.chainId} value={c.chainId}>{c.name}</option>
            ))}
          </select>
          <input
            value={discoverAddress}
            onChange={e => setDiscoverAddress(e.target.value)}
            placeholder="0x token address..."
            className="input flex-1 font-mono text-sm"
          />
          <button onClick={discoverToken} disabled={discovering || !discoverAddress} className="btn-primary flex items-center gap-2 text-sm">
            {discovering ? <Spinner size="sm" className="text-white" /> : <Search className="w-4 h-4" />}
            {discovering ? 'Discovering...' : 'Discover Token'}
          </button>
        </div>

        {discoveredToken && (
          <div className="mt-4 bg-dark-700 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-accent-primary/20 flex items-center justify-center font-bold text-accent-primary">{discoveredToken.symbol?.slice(0, 3)}</div>
              <div>
                <p className="font-semibold text-white">{discoveredToken.name}</p>
                <p className="text-sm text-gray-400">{discoveredToken.symbol} • Decimals: {discoveredToken.decimals}</p>
              </div>
              <div className="ml-auto text-right">
                <p className="font-bold text-white">${discoveredToken.price?.toFixed(8)}</p>
                <p className="text-xs text-gray-400">Current Price</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div><p className="text-gray-400">Liquidity</p><p className="text-white font-medium">${(discoveredToken.liquidity || 0).toLocaleString()}</p></div>
              <div><p className="text-gray-400">Volume 24h</p><p className="text-white font-medium">${(discoveredToken.volume24h || 0).toLocaleString()}</p></div>
              <div><p className="text-gray-400">Chain</p><p className="text-white font-medium">{discoveredToken.chain || chains.find(c => c.chainId === discoverChain)?.name}</p></div>
              <div><p className="text-gray-400">Pairs</p><p className="text-white font-medium">{discoveredToken.pairs?.length || 0} found</p></div>
            </div>
            {(discoveredToken.pairs || []).length > 0 && (
              <div className="mt-3">
                <p className="text-gray-400 text-xs mb-2">Trading Pairs</p>
                <div className="flex flex-wrap gap-2">
                  {discoveredToken.pairs.map((pair, i) => (
                    <div key={i} className="bg-dark-600 rounded-lg px-3 py-1.5 text-xs">
                      <span className="text-white">{pair.pair}</span>
                      <span className="text-gray-400 ml-2">via {pair.dex}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Chain Support Grid */}
      <Card>
        <CardHeader title="Supported Chains" subtitle="All blockchain networks and their current status" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {chains.map(chain => (
            <div key={chain.chainId} className={`rounded-xl p-3 border transition-all cursor-pointer ${chain.status === 'active' ? 'border-dark-500 hover:border-accent-primary/40' : 'border-dark-700 opacity-60'}`}>
              <div className="flex items-center gap-2 mb-2">
                <ChainAvatar name={chain.name} color={chain.color} size="w-7 h-7" />
                <span className="font-medium text-white text-sm">{chain.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{chain.symbol}</span>
                <StatusBadge status={chain.status} />
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {(chain.dexes || []).length} DEXes • {chain.blockTime}s blocks
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
